import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Col, Row, Space, Spin } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { getTimezone } from 'countries-and-timezones';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import { BENGUIDE_MODE_EDIT } from 'modules/auth/constants/globleConstants';
import { usePreviewThumbnailCapture } from 'hooks/usePreviewThumbnailCapture';
import HomeTextWrapper from 'modules/home/components/HomeTextWrapper/HomeTextWrapper';
import ImageColorCustomizer from 'components/ImageColorCustomizer/ImageColorCustomizer';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import { saveSnapshotImage } from 'modules/home/slices/basicInfoSlice';

import {
  getOEMeeting,
  updateOEMeetingSuccess,
  uploadOEVideoStarted,
} from 'modules/home/slices/oeMeetingSlice';
import {
  getBenefitEnrollButton,
  getCheckList,
  clearBasicInfoSuccess,
  getLinkedGuideButton,
  setSpanishGuideAlertOpen,
} from 'modules/home/slices/homeSlice';
import OpenEnrollmentModal from 'modules/home/components/OpenEnrollmentModal/OpenEnrollmentModal';
import OEMeetingState from 'modules/home/enums/OEMeetingState';
import { getOEMeetingState } from 'modules/home/util';

import {
  getEffectiveDates,
  BANNER_DATE_FORMAT,
  getFormattedInputDate,
} from 'utils/dateUtil';

import iconEdit from 'images/icon-edit.svg';
import { ReactComponent as OEMeetingIcon } from 'images/icons/icon-oe-meeting.svg';

import { getBase64Image, saveScreenshot } from 'utils/screenshotUtil';
import { benguideApiV2 } from 'utils/apiUtil';
import { addHttp } from 'utils/httpUtil';

import {
  ImageType,
  TIME_ZONES,
  HOME_CHECK_LIST,
  DEFAULT_BACKGROUND_COLOR,
} from 'modules/home/constants';
import { getOEMeetingVideoRecording } from 'modules/home/services/BenGuideService';
import OEMeetingRecordType from 'modules/home/enums/OEMeetingRecordType';
import BenefitGuideRefreshChannel from 'modules/clients/UPClient/channels/BenefitGuideRefreshChannel';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import {
  EDIT_BENGUIDE_CHANNEL,
  EDIT_BENGUIDE_STARTED,
} from 'modules/clients/UPClient/UPClientConst';
import { NotificationContextProvider } from 'context/NotificationContext';
import BenefitGuideNotificationsChannel from 'modules/clients/UPClient/channels/BenefitGuideNotificationsChannel';
import OEMeetingType from 'modules/home/enums/OEMeetingType';
import ContactSupport from 'components/ContactSupport/ContactSupport';
import { trackEvents } from 'utils/initGA4';
import { getText } from 'utils/stringUtil';
import {
  WATCH_OPEN_ENROLLMENT_MEETING,
  OPEN_ENROLLMENT_MEETING,
  OVERVIEW,
  EXPLORE_YOUR_BENEFITS_GUIDE,
  LEARN_ABOUT_YOUR_BENEFITS,
  EFFECTIVE_FROM,
} from 'constants/languageConstants';
import FooterTextSection from 'components/FooterTextSection';

import styles from './home.module.less';

const Home: FC = () => {
  const location = useLocation();
  const locationState: any = location.state;
  // A "preview" is VIEW mode WITH a previewToken in the URL; a public employee
  // view is VIEW mode WITHOUT a token (read the same way PrivateRoute does).
  const previewToken = new URLSearchParams(location.search).get('previewToken');
  const { benguide } = useAppSelector((state) => state);
  const [isSaveDisable, setSaveDisable] = useState<boolean>(false);

  const {
    backgroundImageReference,
    sectionImageUploadSuccess,
    contentOverviewSuccess,
    contentBenefitSuccess,
    benefitEnrollLinkSuccess,
    commonImageUploadInProgress,
    isSpanishGuideAlertOpen,
  } = useAppSelector((state) => state.home);
  const oeMeetingData = useAppSelector((state) => state.oeMeeting.oeMeeting);
  const oeMeetingSuccess = useAppSelector(
    (state) => state.oeMeeting.oeMeetingSuccess
  );
  const requestTypeOEMeeting = useAppSelector(
    (state) => state.oeMeeting.requestType
  );
  const oeMeetingInProgress = useAppSelector(
    (state) => state.oeMeeting.inProgress
  );
  const { isTablet } = useAppSelector((state) => state.layout);
  const language = useAppSelector((state) => state.benguide.data?.language);

  const dispatch = useAppDispatch();
  const {
    data: benguideData,
    inProgress,
    mode,
    updateBenguideStylesSuccess,
  } = benguide;
  const {
    startDate = '',
    endDate = '',
    storedEffectiveDate,
    overviewText = '',
    checkListText = '',
    styles: styleConfig = {},
    masterId = '',
    overviewImage,
    revision,
    latestRevision,
    oeMeeting,
  } = benguideData || {};
  // Guides saved before the heading was customizable have no value stored, and
  // fall back to the default translated for the guide's own language
  const homeHeading =
    styleConfig?.homeHeading || getText(LEARN_ABOUT_YOUR_BENEFITS, language);
  const isEditMode = mode === BENGUIDE_MODE_EDIT;
  const [node, setNode] = useState<ReactNode>();
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  const [isHomeImageLoaded, setIsHomeImageLoaded] = useState<boolean>(false);
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const [base64, setBase64] = useState<string>('');

  const textRef = useRef<HTMLDivElement>(null);
  const ref = useRef();

  const fetchOeMeeting = useCallback(() => {
    if (!oeMeetingData && oeMeeting && oeMeeting.oemeetingReference) {
      dispatch(getOEMeeting(masterId, oeMeeting.oemeetingReference));
    }
  }, [dispatch, masterId, oeMeetingData, oeMeeting]);

  const createThumbnail = useCallback(() => {
    setIsImageLoaded(false);
    setIsHomeImageLoaded(false);
    setNode(node);
    const data = saveScreenshot(textRef.current);
    data
      .then((src) => {
        if (src) {
          dispatch(saveSnapshotImage(benguideData?.masterId, src));
          dispatch(clearBasicInfoSuccess());
        }
      })
      // Best-effort: a failed capture (e.g. an unauthenticated preview link)
      // must not throw or disrupt the view. The PUT itself is already guarded
      // inside saveSnapshotImage; this guards the html2canvas step.
      .catch(() => {});
  }, [node, benguideData?.masterId, dispatch]);

  // Preview-context capture: a translated (e.g. Spanish) guide only ever got
  // its in-browser thumbnail refreshed in edit mode, so a broker previewing a
  // freshly translated guide would publish a stale/English preview thumbnail.
  // Gate on the PREVIEW context (VIEW mode + a previewToken) so we never fire
  // for plain public employee views, whose anonymous PUT would fail anyway.
  const hasBackgroundImage =
    !isEmpty(styleConfig.backgroundImageUrl) && !isEmpty(backgroundImage);
  // Avoid a blank/partial screenshot by reusing the hero-background image-loaded
  // signal the edit path uses: when a hero background is configured, wait for it
  // to finish painting (isImageLoaded) before capturing; when it is not, the
  // loaded content is enough. The overview image (isHomeImageLoaded) is not made
  // a hard gate here on purpose -- its presence is not knowable from this
  // component (the signal is driven by the HOME_MAIN_IMAGE section, not the
  // benguide field), so requiring it could deadlock the one-shot capture. It is
  // captured best-effort from the DOM by html2canvas (useCORS).
  //
  // UAT-3306: `isImageLoaded` alone is NOT enough. The background effect below
  // calls getBase64Image(backgroundImage) in the same pass that calls
  // setBackgroundImage(url), so its first fetch runs against the stale (empty)
  // URL, resolves at once with no data, and still flips `isImageLoaded` to true.
  // The one-shot preview capture then fired while `base64` (what the wrapper
  // actually paints as `background-image`) was still empty, so the stored
  // thumbnail had no background until the guide was re-edited. The painted
  // data must be present too.
  const heroSettled = hasBackgroundImage
    ? isImageLoaded && !isEmpty(base64)
    : true;
  const isPreviewCaptureReady =
    !inProgress && !isEmpty(benguideData) && !!textRef.current && heroSettled;

  // Fires createThumbnail ONCE per mount when this is a broker preview
  // (VIEW mode + previewToken) and the content/hero image have loaded.
  usePreviewThumbnailCapture({
    mode,
    previewToken,
    ready: isPreviewCaptureReady,
    captureThumbnail: createThumbnail,
  });

  useEffect(() => {
    ref.current = styleConfig && styleConfig.backgroundImageUrl;
  }, [styleConfig]);

  const oeMeetingState = oeMeetingData && getOEMeetingState(oeMeetingData);
  const upClient = useUPClient();

  useEffect(() => {
    if (isEditMode) {
      upClient.postMessage({
        channel: EDIT_BENGUIDE_CHANNEL,
        event: EDIT_BENGUIDE_STARTED,
        data: benguideData.masterId,
      });
    }
  }, [isEditMode, benguideData.masterId, upClient]);

  useEffect(() => {
    if (textRef.current && node !== textRef.current) {
      if (!inProgress && benguideData !== null && isEditMode) {
        if (
          !isEmpty(styleConfig.backgroundImageUrl) &&
          !isEmpty(backgroundImage)
        ) {
          if (
            isImageLoaded ||
            (locationState && locationState.isBasicInfo) ||
            sectionImageUploadSuccess ||
            contentBenefitSuccess ||
            contentOverviewSuccess ||
            oeMeetingSuccess ||
            benefitEnrollLinkSuccess ||
            updateBenguideStylesSuccess ||
            isHomeImageLoaded
          ) {
            createThumbnail();
          }
        } else if (
          (locationState && locationState.isBasicInfo) ||
          sectionImageUploadSuccess ||
          contentBenefitSuccess ||
          contentOverviewSuccess ||
          oeMeetingSuccess ||
          benefitEnrollLinkSuccess ||
          updateBenguideStylesSuccess ||
          isHomeImageLoaded
        ) {
          createThumbnail();
        }
      }
    }
  }, [
    backgroundImage,
    benefitEnrollLinkSuccess,
    benguideData,
    contentBenefitSuccess,
    contentOverviewSuccess,
    dispatch,
    inProgress,
    isEditMode,
    locationState,
    node,
    oeMeetingSuccess,
    sectionImageUploadSuccess,
    styleConfig.backgroundImageUrl,
    updateBenguideStylesSuccess,
    isImageLoaded,
    isHomeImageLoaded,
    createThumbnail,
  ]);

  useEffect(() => {
    if (
      !isEmpty(benguideData) &&
      !isEmpty(styleConfig.backgroundImageUrl) &&
      !commonImageUploadInProgress
    ) {
      setBackgroundImage(
        `${benguideApiV2}/${masterId}/common-images?id=${
          styleConfig.backgroundImageUrl
        }&type=${ImageType.BACKGROUND_IMAGE}&revision=${
          latestRevision || revision
        }`
      );
      getBase64Image(backgroundImage).then((data: any) => {
        setBase64(data);
        setIsImageLoaded(true);
      });
    }
    if (
      isEmpty(styleConfig.backgroundImageUrl) &&
      typeof styleConfig.backgroundImageUrl === 'string'
    ) {
      setBackgroundImage('');
    }
  }, [
    benguideData,
    styleConfig,
    masterId,
    backgroundImageReference,
    backgroundImage,
    dispatch,
    commonImageUploadInProgress,
    latestRevision,
    revision,
  ]);

  useEffect(() => {
    if (masterId) {
      fetchOeMeeting();
      dispatch(getBenefitEnrollButton(masterId));
      dispatch(getLinkedGuideButton(masterId));
    }
  }, [masterId, dispatch, fetchOeMeeting]);

  useEffect(() => {
    if (masterId && requestTypeOEMeeting === updateOEMeetingSuccess.type) {
      fetchOeMeeting();
    }
  }, [
    masterId,
    dispatch,
    fetchOeMeeting,
    requestTypeOEMeeting,
    oeMeetingData,
    isEditMode,
    oeMeeting,
  ]);

  useEffect(() => {
    if (masterId) {
      dispatch(getCheckList(masterId, HOME_CHECK_LIST));
    }
    // eslint-disable-next-line
  }, [masterId, dispatch]);

  const checkHomeImageLoad = () => {
    setIsHomeImageLoaded(true);
  };

  const getOEMeetingTime = () => {
    if (oeMeetingData && oeMeetingData.meetingTime && oeMeetingData.timezone) {
      return getFormattedInputDate(
        oeMeetingData.meetingTime,
        BANNER_DATE_FORMAT,
        language
      );
    }
    return '';
  };

  const getTimezoneAbbreviationOrText = () => {
    let abbreviationText = '';
    if (oeMeetingData && oeMeetingData.timezone) {
      const zone = TIME_ZONES.find(
        (timezone) => timezone.id === oeMeetingData.timezone
      );
      if (zone) {
        if (zone.abbreviation) {
          abbreviationText = zone.abbreviation;
        } else {
          const timezoneObj = getTimezone(zone.id);
          const timezoneText = zone.name;
          abbreviationText = timezoneObj
            ? `(GMT${timezoneObj.dstOffsetStr}) ${timezoneText}`
            : `(GMT ${timezoneText}`;
        }
      }
    }
    return abbreviationText;
  };

  const getOEMeetingStateDisplayText = (): OEMeetingState | '' => {
    if (oeMeetingState) {
      if (oeMeetingState.inProgress) {
        return OEMeetingState.IN_PROGRESS;
      } else if (oeMeetingState.recorded) {
        return OEMeetingState.RECORDED;
      }
    }
    return '';
  };

  const openOEMeetingLink = () => {
    if (oeMeetingState && oeMeetingData) {
      if (oeMeetingState.inProgress && oeMeetingData.meetingUrl) {
        window.open(addHttp(oeMeetingData.meetingUrl), '_blank');
      } else if (
        oeMeetingState.recorded &&
        oeMeetingData.meetingRecordUrl &&
        oeMeetingData.oeMeetingRecordType === OEMeetingRecordType.URL
      ) {
        window.open(addHttp(oeMeetingData.meetingRecordUrl), '_blank');
      } else if (
        oeMeetingState.recorded &&
        oeMeetingData.oeVideoFile &&
        oeMeetingData.oeMeetingRecordType === OEMeetingRecordType.VIDEO_FILE
      ) {
        masterId &&
          window.open(getOEMeetingVideoRecording(masterId, false), '_blank');
      } else if (
        oeMeetingData.oeMeetingType === OEMeetingType.FUTURE &&
        oeMeetingData.meetingUrl
      ) {
        window.open(addHttp(oeMeetingData.meetingUrl));
      }

      trackEvents({
        category: 'Open Enrollment Meeting',
        action: 'open_enrollment_meeting',
        label: 'Open Enrollment Meeting',
      });
    }
  };

  if (inProgress) {
    return (
      <Space size="middle">
        <Spin size="large" />
      </Space>
    );
  }

  const oeMeetingStatus = getOEMeetingStateDisplayText();
  const addOEMeetingBtnInProgressCheck =
    requestTypeOEMeeting !== uploadOEVideoStarted.type
      ? !oeMeetingInProgress
      : true;
  const oeMeetingAddBtnStyle = `${styles.customizerWrapper} ${styles.oeMeeting} ${styles.oeMeetingAdd}`;
  const oeMeetingBannerStyle = `${styles.oeMeetingWrapper} ${
    isEditMode ? '' : styles.viewMode
  }`;

  return (
    <NotificationContextProvider isDisableSave={isSaveDisable}>
      <div
        className={styles.homeWrapper}
        ref={textRef}
        style={{
          backgroundColor: `${
            isEmpty(backgroundImage)
              ? styleConfig.backgroundColor
              : DEFAULT_BACKGROUND_COLOR
          }`,
          backgroundImage: `${
            !isEmpty(backgroundImage) &&
            !isEmpty(styleConfig.backgroundImageUrl)
              ? `url(${base64})`
              : ''
          }`,
        }}
      >
        <BenefitGuideRefreshChannel />
        <BenefitGuideNotificationsChannel
          setDisableSave={() => setSaveDisable(true)}
        />
        {language === 'SPANISH' && isEditMode && isSpanishGuideAlertOpen && (
          <AlertMessage
            timed={false}
            wrapperClassName={styles.spanishAlertMessage}
            type="error"
            closeAlert={() => dispatch(setSpanishGuideAlertOpen(false))}
            message={
              <>
                <b>Spanish Microsite Editing:</b>
                <u>
                  {' '}
                  Any changes made to this Spanish Microsite will not be
                  reflected in the English version. Likewise, updates made to
                  the English Microsite will not affect this Spanish version.
                  The two microsites are fully independent after creation.
                </u>
              </>
            }
          />
        )}
        <Row>
          <Col span={24}>
            {isEditMode ? (
              <EditableHotspot
                alwaysVisible={false}
                className={styles.headingHotspot}
                editIcon={<img src={iconEdit} alt="edit" />}
                customModal={(visible: boolean, onClose: Function) => (
                  <ImageColorCustomizer
                    isOpenModal={visible}
                    closeModal={onClose}
                    benguideData={benguideData}
                    backgroundImage={backgroundImage}
                  />
                )}
              >
                <strong className={styles.benguideTitle}>{homeHeading}</strong>
              </EditableHotspot>
            ) : (
              <strong className={styles.benguideTitle}>{homeHeading}</strong>
            )}
          </Col>
          {isEditMode && (
            <Col
              offset={12}
              className={`${styles.customizerWrapper} ${styles.headingCustomizer}`}
            >
              <EditableHotspot
                alwaysVisible={true}
                customModal={(visible: boolean, onClose: Function) => (
                  <ImageColorCustomizer
                    isOpenModal={visible}
                    closeModal={onClose}
                    benguideData={benguideData}
                    backgroundImage={backgroundImage}
                  />
                )}
              >
                <div className={styles.customizerHotspot}>
                  Edit Background & Colors
                  <img src={iconEdit} alt="edit" />
                </div>
              </EditableHotspot>
            </Col>
          )}
        </Row>
        {storedEffectiveDate ? (
          <span className={styles.enrollmentDateText}>
            <span>
              {`${getText(EFFECTIVE_FROM, language)} ${getFormattedInputDate(
                storedEffectiveDate,
                'LL',
                language
              )}`}
            </span>
          </span>
        ) : (
          startDate &&
          endDate && (
            <span className={styles.enrollmentDateText}>
              {<span>{getEffectiveDates(startDate, endDate, language)}</span>}
            </span>
          )
        )}
        <Row>
          {isEditMode &&
            addOEMeetingBtnInProgressCheck &&
            (!oeMeetingData || oeMeetingState?.finished) && (
              <Col>
                <div
                  className={`${styles.customizerWrapper} ${styles.oeMeeting}`}
                >
                  <EditableHotspot
                    alwaysVisible={true}
                    customModal={(visible: boolean, onClose: Function) => (
                      <OpenEnrollmentModal
                        isOpenModal={visible}
                        closeModal={onClose}
                        benGuideId={masterId}
                        revision={revision}
                      />
                    )}
                  >
                    <div className={styles.customizerHotspot}>
                      Add Open Enrollment Meeting Info
                      <img src={iconEdit} alt="edit" />
                    </div>
                  </EditableHotspot>
                </div>
              </Col>
            )}
        </Row>
        {!oeMeetingState?.finished &&
          oeMeetingData &&
          ((oeMeetingData.displayMeetingInfo && !isEditMode) || isEditMode) && (
            <div
              className={
                oeMeetingData.displayMeetingInfo
                  ? oeMeetingBannerStyle
                  : oeMeetingAddBtnStyle
              }
            >
              <EditableHotspot
                alwaysVisible={!oeMeetingData.displayMeetingInfo}
                editText={
                  oeMeetingData.displayMeetingInfo
                    ? 'Add or Edit Open Enrollment Meeting Info'
                    : undefined
                }
                editIcon={
                  oeMeetingData.displayMeetingInfo ? (
                    <img src={iconEdit} alt="edit" />
                  ) : undefined
                }
                viewModeAction={openOEMeetingLink}
                customModal={(visible: boolean, onClose: Function) => (
                  <OpenEnrollmentModal
                    isOpenModal={visible}
                    closeModal={onClose}
                    benGuideId={masterId}
                    oeMeeting={oeMeetingData}
                    revision={revision}
                  />
                )}
              >
                {oeMeetingData.displayMeetingInfo ? (
                  <div className={styles.oeMeetingSection}>
                    <OEMeetingIcon className={styles.oeMeetingIcon} />
                    <div>
                      {oeMeetingStatus === OEMeetingState.RECORDED
                        ? getText(WATCH_OPEN_ENROLLMENT_MEETING, language)
                        : `${oeMeetingStatus} ${getText(
                            OPEN_ENROLLMENT_MEETING,
                            language
                          )}: ${getOEMeetingTime()} ${getTimezoneAbbreviationOrText()}`}
                    </div>
                  </div>
                ) : (
                  <div className={styles.customizerHotspot}>
                    Add Open Enrollment Meeting Info
                    <img src={iconEdit} alt="edit" />
                  </div>
                )}
              </EditableHotspot>
            </div>
          )}
        <HomeTextWrapper
          isEditMode={isEditMode}
          isOverview={true}
          initialTextOverview={overviewText}
          title={getText(OVERVIEW, language)}
          benguideData={benguideData}
          overviewImage={overviewImage}
          checkHomeImageLoad={checkHomeImageLoad}
        />
        <HomeTextWrapper
          isOverview={false}
          initialTextBenefit={checkListText}
          title={getText(EXPLORE_YOUR_BENEFITS_GUIDE, language)}
          benguideData={benguideData}
        />
        {isTablet && (
          <ContactSupport
            className={styles.contactSupportContainer}
            btnWrapperClassName={styles.supportBtnWrapper}
          />
        )}
        <FooterTextSection masterId={masterId} isEditMode={isEditMode} />
      </div>
    </NotificationContextProvider>
  );
};

export default Home;
